'use client';

import { c as _c } from "react/compiler-runtime";
import { Dropdown, LoadingIndicator, TextField, TextPassage } from '@petsmart-ui/sparky';
import { useField } from 'formik';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useOnClickOutside } from '@libs/common-next';
import type { AddressFormInterface, SuggestedAddressInterface, SuggestionResponseInterface } from './types';
import { getFieldErrorMessage } from './utils';
const componentClassname = 'street-address-line';
export interface StreetAddressLineInterface {
  formLibrary?: 'formik' | 'rhf';
  dropdownIsOpen: boolean;
  setDropdownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  suggestedAddresses: SuggestedAddressInterface[] | null;
  handleSuggestedAddressClick: (address: SuggestedAddressInterface) => void | Promise<void>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  checkAddressSuggestions: (value: string) => Promise<SuggestionResponseInterface | null>;
  setSuggestedAddresses: React.Dispatch<React.SetStateAction<SuggestedAddressInterface[] | null>>;
}
export function StreetAddressLine(t0) {
  const $ = _c(17);
  const {
    formLibrary: t1,
    dropdownIsOpen,
    suggestedAddresses,
    handleSuggestedAddressClick,
    loading,
    setLoading,
    checkAddressSuggestions,
    setDropdownIsOpen,
    setSuggestedAddresses
  } = t0;
  const formLibrary = t1 === undefined ? "formik" : t1;
  const timeoutIdRef = useRef(null);
  const containerRef = useRef(null);
  let t2;
  if ($[0] !== setDropdownIsOpen) {
    t2 = () => {
      setDropdownIsOpen(false);
    };
    $[0] = setDropdownIsOpen;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearSuggestions = t2;
  useOnClickOutside(containerRef, clearSuggestions);
  const formatMatchedText = _temp2;
  let t3;
  if ($[2] !== checkAddressSuggestions || $[3] !== setDropdownIsOpen || $[4] !== setLoading || $[5] !== setSuggestedAddresses) {
    t3 = e => {
      const {
        value: inputValue
      } = e.target;
      setSuggestedAddresses(null);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (inputValue) {
        timeoutIdRef.current = setTimeout(() => {
          setLoading(true);
          checkAddressSuggestions(inputValue);
        }, 500);
        setDropdownIsOpen(true);
      } else {
        setDropdownIsOpen(false);
      }
    };
    $[2] = checkAddressSuggestions;
    $[3] = setDropdownIsOpen;
    $[4] = setLoading;
    $[5] = setSuggestedAddresses;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleStreetInput = t3;
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
    t5 = [];
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== dropdownIsOpen || $[10] !== formLibrary || $[11] !== handleStreetInput || $[12] !== handleSuggestedAddressClick || $[13] !== loading || $[14] !== setDropdownIsOpen || $[15] !== suggestedAddresses) {
    t6 = formLibrary === "rhf" ? <StreetAddressLineRHF dropdownIsOpen={dropdownIsOpen} suggestedAddresses={suggestedAddresses} handleSuggestedAddressClick={handleSuggestedAddressClick} loading={loading} setDropdownIsOpen={setDropdownIsOpen} formatMatchedText={formatMatchedText} handleStreetInput={handleStreetInput} containerRef={containerRef} /> : <StreetAddressLineFormik dropdownIsOpen={dropdownIsOpen} suggestedAddresses={suggestedAddresses} handleSuggestedAddressClick={handleSuggestedAddressClick} loading={loading} setDropdownIsOpen={setDropdownIsOpen} formatMatchedText={formatMatchedText} handleStreetInput={handleStreetInput} containerRef={containerRef} />;
    $[9] = dropdownIsOpen;
    $[10] = formLibrary;
    $[11] = handleStreetInput;
    $[12] = handleSuggestedAddressClick;
    $[13] = loading;
    $[14] = setDropdownIsOpen;
    $[15] = suggestedAddresses;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp2(text, matches) {
  const formattedText = [];
  let currentIndex = 0;
  matches.sort(_temp);
  matches.forEach(t0 => {
    const [start, end] = t0;
    if (currentIndex < start) {
      formattedText.push(text.slice(currentIndex, start));
    }
    formattedText.push(<strong key={start}>{text.slice(start, end)}</strong>);
    currentIndex = end;
  });
  if (currentIndex < text.length) {
    formattedText.push(text.slice(currentIndex));
  }
  return formattedText.length > 0 ? formattedText : text;
}
function _temp(t0, t1) {
  const [startA] = t0;
  const [startB] = t1;
  return startA - startB;
}
type StreetAddressFieldBinding = {
  name: string;
  value: string;
  isError: boolean;
  fieldNote?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
};
type RenderStreetAddressProps = {
  dropdownIsOpen: boolean;
  suggestedAddresses: SuggestedAddressInterface[] | null;
  handleSuggestedAddressClick: (address: SuggestedAddressInterface) => void | Promise<void>;
  loading: boolean;
  setDropdownIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  formatMatchedText: (text: SuggestedAddressInterface['suggestion'], matches: SuggestedAddressInterface['matched']) => string | (string | React.JSX.Element)[];
  handleStreetInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
};
function StreetAddressFieldView({
  binding,
  dropdownIsOpen,
  suggestedAddresses,
  handleSuggestedAddressClick,
  loading,
  setDropdownIsOpen,
  formatMatchedText,
  handleStreetInput,
  containerRef
}: Readonly<RenderStreetAddressProps & {
  binding: StreetAddressFieldBinding;
}>) {
  return <div className={componentClassname} ref={containerRef}>
            <TextField type="text" data-testid="streetAddressInput" data-cs-mask label="Street address" name={binding.name} ref={binding.inputRef} isError={binding.isError} fieldNote={binding.fieldNote} value={binding.value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
      binding.onChange(e);
      handleStreetInput(e);
    }} onFocus={() => {
      setDropdownIsOpen(true);
    }} onBlur={binding.onBlur} />
            {dropdownIsOpen && suggestedAddresses && binding.value.length > 0 && <Dropdown className={`${componentClassname}__dropdown`} data-cs-mask>
                    {suggestedAddresses.length > 0 ? suggestedAddresses.map(address => <TextPassage key={address.format} className={`${componentClassname}__fpo`} size="lg" onClick={() => handleSuggestedAddressClick(address)} data-testid="address-fpo">
                                  {formatMatchedText(address.suggestion, address.matched)}
                              </TextPassage>) : !loading && <TextPassage key="no-results-found" className={`${componentClassname}__fpo`}>
                                  No results found.
                              </TextPassage>}
                </Dropdown>}
            {loading && <LoadingIndicator iconSize="lg" className={`${componentClassname}__loader`} />}
        </div>;
}
function StreetAddressLineFormik(props) {
  const $ = _c(12);
  const [t0, t1] = useField("address.streetAddress");
  const {
    value,
    onChange,
    onBlur
  } = t0;
  const {
    touched,
    error
  } = t1;
  const t2 = touched && !!error;
  let t3;
  if ($[0] !== error || $[1] !== touched) {
    t3 = touched ? getFieldErrorMessage(error) : undefined;
    $[0] = error;
    $[1] = touched;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onBlur || $[4] !== onChange || $[5] !== t2 || $[6] !== t3 || $[7] !== value) {
    t4 = {
      name: "address.streetAddress",
      value,
      isError: t2,
      fieldNote: t3,
      onChange,
      onBlur
    };
    $[3] = onBlur;
    $[4] = onChange;
    $[5] = t2;
    $[6] = t3;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== props || $[10] !== t4) {
    t5 = <StreetAddressFieldView {...props} binding={t4} />;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function StreetAddressLineRHF(props) {
  const $ = _c(17);
  const {
    register,
    watch,
    formState: t0
  } = useFormContext();
  const {
    errors,
    touchedFields,
    isSubmitted
  } = t0;
  let t1;
  if ($[0] !== register) {
    t1 = register("address.streetAddress");
    $[0] = register;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const streetAddressField = t1;
  let t2;
  if ($[2] !== watch) {
    t2 = watch("address.streetAddress") ?? "";
    $[2] = watch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const value = t2;
  const t3 = (errors.address as {
    streetAddress?: unknown;
  } | undefined)?.streetAddress;
  let t4;
  if ($[4] !== t3) {
    t4 = getFieldErrorMessage(t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const streetAddressError = t4;
  const showStreetAddressError = !!(touchedFields.address?.streetAddress || isSubmitted) && !!streetAddressError;
  const t5 = showStreetAddressError ? streetAddressError : undefined;
  let t6;
  if ($[6] !== showStreetAddressError || $[7] !== streetAddressField.name || $[8] !== streetAddressField.onBlur || $[9] !== streetAddressField.onChange || $[10] !== streetAddressField.ref || $[11] !== t5 || $[12] !== value) {
    t6 = {
      name: streetAddressField.name,
      value,
      isError: showStreetAddressError,
      fieldNote: t5,
      onChange: streetAddressField.onChange,
      onBlur: streetAddressField.onBlur,
      inputRef: streetAddressField.ref
    };
    $[6] = showStreetAddressError;
    $[7] = streetAddressField.name;
    $[8] = streetAddressField.onBlur;
    $[9] = streetAddressField.onChange;
    $[10] = streetAddressField.ref;
    $[11] = t5;
    $[12] = value;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== props || $[15] !== t6) {
    t7 = <StreetAddressFieldView {...props} binding={t6} />;
    $[14] = props;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}